import React, { useState, useEffect } from 'react';
import {
  X, ChevronDown, ChevronRight, Upload, Plus, MoreVertical,
  FileText, Eye, Trash2, Download, FolderPlus, Loader2, Images, Pencil
} from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FOLDER_LABELS = {
  Business_Identity:             'Business Identity',
  Online_Presence_Branding:      'Online Presence & Branding',
  Business_Formation:            'Business Formation',
  Medicare_Medicaid_Enrollment:  'Medicare / Medicaid Enrollment',
  Accreditation_Compliance:      'Accreditation & Compliance',
  Physical_Location_Operations:  'Physical Location & Operations',
  Billing_Revenue_Cycle:         'Billing & Revenue Cycle',
  Product_Lines_Equipment:       'Product Lines & Equipment',
  Staffing_Training:             'Staffing & Training',
  Referrals_Marketing:           'Referrals & Marketing',
  Legal_Contracts:               'Legal & Contracts',
};

const FOLDER_COLORS = {
  Business_Identity:            '#3b82f6',
  Online_Presence_Branding:     '#8b5cf6',
  Business_Formation:           '#64748b',
  Medicare_Medicaid_Enrollment: '#0891b2',
  Accreditation_Compliance:     '#059669',
  Physical_Location_Operations: '#f59e0b',
  Billing_Revenue_Cycle:        '#10b981',
  Product_Lines_Equipment:      '#ef4444',
  Staffing_Training:            '#6366f1',
  Referrals_Marketing:          '#ec4899',
  Legal_Contracts:              '#475569',
};

const fmt = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1048576) return `${(bytes/1024).toFixed(1)}KB`;
  return `${(bytes/1048576).toFixed(1)}MB`;
};

const DMEMediaPanel = ({ projectId, onClose, getAuthHeaders }) => {
  const [folders, setFolders]           = useState([]);
  const [loading, setLoading]           = useState(true);  // start loading immediately
  const [expanded, setExpanded]         = useState({});
  const [dragOver, setDragOver]         = useState(null);
  const [previewFile, setPreviewFile]   = useState(null);
  const [renameFile, setRenameFile]     = useState(null);  // { path, name, folder }
  const [renameName, setRenameName]     = useState('');
  const [renaming, setRenaming]         = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [uploadDialog, setUploadDialog] = useState(null);
  const [uploadTitle, setUploadTitle]   = useState('');
  const [saving, setSaving]             = useState(false);
  // Slide-in animation: start off-screen, animate to 0 after mount
  const [slideIn, setSlideIn]           = useState(false);

  useEffect(() => {
    // Trigger slide-in after mount
    requestAnimationFrame(() => setSlideIn(true));
    // Load folders
    loadMedia();
  }, []); // eslint-disable-line

  const loadMedia = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/projects/${projectId}/dme-media`,
        getAuthHeaders()
      );
      const fl = res.data?.folders || [];
      setFolders(fl);
      // Expand all folders by default
      const exp = {};
      fl.forEach(f => { exp[f.name] = true; });
      setExpanded(exp);
    } catch (e) {
      toast.error('Failed to load media files');
    } finally {
      setLoading(false);
    }
  };

  const toggleFolder = (name) =>
    setExpanded(prev => ({ ...prev, [name]: !prev[name] }));

  // ── Upload via hidden file input ──────────────────────────
  const pickFile = (folderName) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (file) prepareUpload(folderName, file);
    };
    input.click();
  };

  const prepareUpload = (folderName, file) => {
    const isImage = file.type.startsWith('image/');
    const previewUrl = isImage ? URL.createObjectURL(file) : null;
    const defaultTitle = file.name.replace(/\.[^/.]+$/, '');
    setUploadTitle(defaultTitle);
    setUploadDialog({ folder: folderName, file, previewUrl });
  };

  const confirmUpload = async () => {
    if (!uploadDialog || !uploadTitle.trim()) return;
    setSaving(true);
    const fd = new FormData();
    fd.append('folder', uploadDialog.folder);
    fd.append('title', uploadTitle.trim());
    fd.append('file', uploadDialog.file);
    try {
      await axios.post(
        `${API_URL}/projects/${projectId}/dme-media/upload`,
        fd,
        { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('File uploaded & saved');
      setUploadDialog(null);
      setUploadTitle('');
      loadMedia();
    } catch {
      toast.error('Upload failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Drag & Drop ───────────────────────────────────────────
  const onDragOver  = (e, name) => { e.preventDefault(); setDragOver(name); };
  const onDragLeave = ()        => setDragOver(null);
  const onDrop      = (e, name) => {
    e.preventDefault(); setDragOver(null);
    const file = e.dataTransfer.files?.[0];
    if (file) prepareUpload(name, file);
  };

  // ── Delete file ───────────────────────────────────────────
  const deleteFile = async (path) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await axios.delete(
        `${API_URL}/projects/${projectId}/dme-media/file`,
        { ...getAuthHeaders(), data: { path } }
      );
      toast.success('File deleted');
      loadMedia();
    } catch { toast.error('Delete failed'); }
  };

  // ── Rename file ───────────────────────────────────────────
  const openRename = (file, folder) => {
    const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
    setRenameFile({ ...file, folder });
    setRenameName(nameWithoutExt);
  };

  const confirmRename = async (e) => {
    e.preventDefault();
    if (!renameName.trim() || !renameFile) return;
    setRenaming(true);
    try {
      const ext = renameFile.name.includes('.') ? '.' + renameFile.name.split('.').pop() : '';
      const newName = renameName.trim() + ext;
      await axios.post(
        `${API_URL}/projects/${projectId}/dme-media/rename`,
        { old_path: renameFile.path, new_name: newName, folder: renameFile.folder },
        getAuthHeaders()
      );
      toast.success('File renamed');
      setRenameFile(null);
      setRenameName('');
      loadMedia();
    } catch {
      toast.error('Rename failed');
    } finally {
      setRenaming(false);
    }
  };

  // ── Create custom folder ─────────────────────────────────
  const createFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;
    try {
      await axios.post(
        `${API_URL}/projects/${projectId}/dme-media/folder`,
        { name: newFolderName.trim() },
        getAuthHeaders()
      );
      toast.success('Folder created');
      setNewFolderOpen(false);
      setNewFolderName('');
      loadMedia();
    } catch { toast.error('Failed to create folder'); }
  };

  return (
    <>
      {/* ── Backdrop ───────────────────────────────────── */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      />

      {/* ── Slide panel ────────────────────────────────── */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: '420px',
          zIndex: 50,
          display: 'flex',
          flexDirection: 'column',
          background: '#fff',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
          borderLeft: '1px solid #e2e8f0',
          transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
          transform: slideIn ? 'translateX(0)' : 'translateX(100%)',
          overflowY: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg,#1e293b,#334155)', flexShrink: 0 }}>
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2 text-white">
              <Images size={18} />
              <span className="font-semibold text-sm">DME Media Center</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setNewFolderOpen(true)}
                className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/20 transition-colors"
                title="Add custom folder"
              >
                <FolderPlus size={15} />
              </button>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/20 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>
          <p className="px-4 pb-2.5 text-xs text-slate-400">
            Click ⬆ to upload · Drag & drop files · Sorted A–Z
          </p>
        </div>

        {/* Folder list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-slate-400" size={28} />
            </div>
          ) : folders.length === 0 ? (
            <div className="text-center py-20 text-slate-400 text-sm px-6">
              No folders loaded.
              <button onClick={loadMedia} className="block mx-auto mt-2 text-blue-500 underline text-xs">
                Retry
              </button>
            </div>
          ) : (
            <div>
              {folders.map((folder) => {
                const label   = FOLDER_LABELS[folder.name] || folder.name.replace(/_/g, ' ');
                const color   = FOLDER_COLORS[folder.name] || '#64748b';
                const isExp   = !!expanded[folder.name];
                const isDrag  = dragOver === folder.name;

                return (
                  <div
                    key={folder.name}
                    style={{ borderBottom: '1px solid #f1f5f9' }}
                  >
                    {/* Folder row */}
                    <div
                      onClick={() => toggleFolder(folder.name)}
                      onDragOver={(e) => onDragOver(e, folder.name)}
                      onDragLeave={onDragLeave}
                      onDrop={(e) => onDrop(e, folder.name)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 12px',
                        cursor: 'pointer',
                        background: isDrag ? '#eff6ff' : '#fff',
                        transition: 'background 0.15s',
                        userSelect: 'none',
                      }}
                      className="hover:bg-slate-50"
                    >
                      {/* Color dot */}
                      <span
                        style={{
                          width: 10, height: 10, borderRadius: '50%',
                          background: color, flexShrink: 0
                        }}
                      />
                      <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {label}
                      </span>
                      <span style={{ fontSize: 11, color: '#94a3b8', marginRight: 4 }}>
                        {folder.files.length}
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); pickFile(folder.name); }}
                        title="Upload to this folder"
                        style={{ padding: '3px 5px', borderRadius: 4, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer' }}
                        className="hover:bg-slate-200 hover:text-slate-700"
                      >
                        <Upload size={13} />
                      </button>
                      {isExp
                        ? <ChevronDown  size={13} style={{ color: '#94a3b8', flexShrink: 0 }} />
                        : <ChevronRight size={13} style={{ color: '#94a3b8', flexShrink: 0 }} />
                      }
                    </div>

                    {/* Accordion content */}
                    {isExp && (
                      <div
                        onDragOver={(e) => onDragOver(e, folder.name)}
                        onDragLeave={onDragLeave}
                        onDrop={(e) => onDrop(e, folder.name)}
                        style={{
                          background: isDrag ? '#eff6ff' : '#f8fafc',
                          borderTop: '1px solid #f1f5f9',
                          outline: isDrag ? '2px dashed #3b82f6' : 'none',
                          outlineOffset: '-4px',
                        }}
                      >
                        {folder.files.length === 0 ? (
                          /* Empty drop zone */
                          <button
                            onClick={() => pickFile(folder.name)}
                            style={{
                              display: 'flex', flexDirection: 'column', alignItems: 'center',
                              width: 'calc(100% - 16px)', margin: '8px',
                              padding: '16px 8px', border: '2px dashed #cbd5e1', borderRadius: 8,
                              background: 'none', cursor: 'pointer', color: '#94a3b8', transition: 'all 0.15s'
                            }}
                            className="hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50/50"
                          >
                            <Upload size={18} style={{ marginBottom: 4, opacity: 0.6 }} />
                            <span style={{ fontSize: 11 }}>Drop files or click to upload</span>
                          </button>
                        ) : (
                          <div style={{ padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {folder.files.map((file) => (
                              <div
                                key={file.path}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 8,
                                  padding: '6px 8px', background: '#fff',
                                  border: '1px solid #e2e8f0', borderRadius: 8,
                                }}
                                className="group hover:border-slate-300 hover:shadow-sm transition-all"
                              >
                                {/* Thumb or icon */}
                                {file.is_image && file.url ? (
                                  <img
                                    src={file.url}
                                    alt={file.name}
                                    style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover', border: '1px solid #e2e8f0', flexShrink: 0 }}
                                  />
                                ) : (
                                  <div style={{ width: 32, height: 32, borderRadius: 6, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <FileText size={14} style={{ color: '#94a3b8' }} />
                                  </div>
                                )}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <p style={{ fontSize: 12, fontWeight: 500, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {file.name}
                                  </p>
                                  <p style={{ fontSize: 10, color: '#94a3b8' }}>{fmt(file.size)}</p>
                                </div>
                                {/* 3-dot menu */}
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      style={{ padding: '3px', borderRadius: 4, border: 'none', background: 'none', cursor: 'pointer', color: '#64748b' }}
                                      className="hover:bg-slate-200 transition-all flex-shrink-0"
                                    >
                                      <MoreVertical size={14} />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-40">
                                    <DropdownMenuItem onClick={() => setPreviewFile(file)}>
                                      <Eye size={13} className="mr-2" /> Preview
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openRename(file, folder.name)}>
                                      <Pencil size={13} className="mr-2" /> Edit Name
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                      <a href={file.url} download target="_blank" rel="noopener noreferrer" className="flex items-center">
                                        <Download size={13} className="mr-2" /> Download
                                      </a>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      className="text-red-600 focus:text-red-600"
                                      onClick={() => deleteFile(file.path)}
                                    >
                                      <Trash2 size={13} className="mr-2" /> Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            ))}
                            {/* Add more button */}
                            <button
                              onClick={() => pickFile(folder.name)}
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                gap: 4, padding: '6px', border: '1px dashed #cbd5e1',
                                borderRadius: 8, background: 'none', cursor: 'pointer',
                                fontSize: 11, color: '#94a3b8',
                              }}
                              className="hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50/50 transition-all"
                            >
                              <Plus size={11} /> Add file
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Upload title dialog ─────────────────────────── */}
      <Dialog open={!!uploadDialog} onOpenChange={() => { setUploadDialog(null); setUploadTitle(''); }}>
        <DialogContent className="max-w-sm" style={{ zIndex: 60 }}>
          <DialogHeader>
            <DialogTitle>Name this file</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {uploadDialog?.previewUrl && (
              <img src={uploadDialog.previewUrl} alt="preview"
                className="w-full h-36 object-contain rounded-xl bg-slate-50 border" />
            )}
            {!uploadDialog?.previewUrl && uploadDialog?.file && (
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border">
                <FileText size={22} className="text-slate-400" />
                <p className="text-sm text-slate-600 truncate">{uploadDialog.file.name}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>File Title</Label>
              <Input
                value={uploadTitle}
                onChange={e => setUploadTitle(e.target.value)}
                placeholder="Enter a descriptive title"
                autoFocus
                onKeyDown={e => e.key === 'Enter' && confirmUpload()}
              />
              <p className="text-xs text-slate-400">
                Folder: <strong>{FOLDER_LABELS[uploadDialog?.folder] || uploadDialog?.folder?.replace(/_/g,' ')}</strong>
                &nbsp;· Sorted A–Z
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setUploadDialog(null); setUploadTitle(''); }}>
                Cancel
              </Button>
              <Button className="flex-1 bg-slate-900 hover:bg-slate-800" disabled={saving || !uploadTitle.trim()} onClick={confirmUpload}>
                {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />}
                Upload
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Add custom folder dialog ─────────────────────── */}
      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent className="max-w-sm" style={{ zIndex: 60 }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderPlus size={17} /> Add Custom Folder
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={createFolder} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Folder Name</Label>
              <Input value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                placeholder="e.g. Client Photos, Agreements 2025" autoFocus required />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setNewFolderOpen(false)}>Cancel</Button>
              <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800">Create</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Image preview dialog ─────────────────────────── */}
      <Dialog open={!!previewFile} onOpenChange={() => setPreviewFile(null)}>
        <DialogContent className="max-w-2xl" style={{ zIndex: 60 }}>
          <DialogHeader><DialogTitle>{previewFile?.name}</DialogTitle></DialogHeader>
          {previewFile?.is_image && previewFile?.url ? (
            <img src={previewFile.url} alt={previewFile.name} className="w-full max-h-[60vh] object-contain rounded-xl" />
          ) : (
            <div className="flex flex-col items-center justify-center py-10 text-slate-400">
              <FileText size={48} className="mb-3 opacity-40" />
              <p className="text-sm">Preview not available for this file type</p>
            </div>
          )}
          <div className="flex justify-between items-center pt-2">
            <span className="text-xs text-slate-400">{fmt(previewFile?.size)}</span>
            <Button asChild variant="outline" size="sm">
              <a href={previewFile?.url} target="_blank" rel="noopener noreferrer" download>
                <Download size={14} className="mr-1" /> Download
              </a>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Rename dialog ────────────────────────────────── */}
      <Dialog open={!!renameFile} onOpenChange={(open) => { if (!open) { setRenameFile(null); setRenameName(''); } }}>
        <DialogContent className="max-w-sm" style={{ zIndex: 60 }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil size={16} className="text-blue-500" /> Edit File Name
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={confirmRename} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-sm text-slate-600">New name for: <span className="font-medium text-slate-800">{renameFile?.name}</span></Label>
              <Input
                value={renameName}
                onChange={e => setRenameName(e.target.value)}
                placeholder="Enter new name"
                autoFocus
                required
              />
              <p className="text-xs text-slate-400">File extension will be preserved automatically.</p>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setRenameFile(null)}>Cancel</Button>
              <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800" disabled={renaming || !renameName.trim()}>
                {renaming ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Pencil size={14} className="mr-1" />}
                Rename
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DMEMediaPanel;
